/**
 * Alarm sound CATEGORIES — the semantic layer that decides which learnable sound
 * an alarm gets by default.
 *
 * There are a lot of alarm keys (anchor, depth, grounding, AIS CPA, four GNSS
 * ones, tide, battery and so on), but a skipper can't learn a dozen different
 * beeps by ear. So we fold them into a handful of CATEGORIES, each with its own
 * distinct sound, so the ear only has to learn five things:
 *
 *   - danger     depth / grounding / safety-contour / charted hazard / clearing
 *                lines. "You're standing into danger" — a low, firm, serious pulse.
 *   - collision  a closing AIS target (CPA). A distinct sonar-ish two-tone so
 *                traffic never sounds like the depth alarm.
 *   - gnss       lost fix / poor accuracy / possible spoofing. A softer, higher
 *                caution chirp — the fix can't be trusted, but it's not a grounding.
 *   - anchor     the anchor is dragging. An insistent mid-tone that nags.
 *   - notice     low battery, tide height, waypoint arrival, off-track, a nearby
 *                (not closing) AIS target. Low-priority nudges — a gentle blip.
 *
 * This module is pure and holds no audio: it just maps a key to a category, and a
 * category to the tone + default volume it should play. The store (alarmSounds.ts)
 * builds the shipped defaults from it, and the player (alarmSound.ts) plays the
 * tone. Keeping the mapping here, away from React and the AudioContext, is what
 * makes it unit-testable.
 */
import type { AlarmKey } from '@/safety/alarmState';
import type { ToneId } from './alarmTones';

/** The five alarm sound categories. MOB is deliberately NOT one of these — it's a
 *  confirmation, not an alarm, and has its own sound (see MOB_CONFIRM_TONE). */
export type AlarmSoundCategory = 'danger' | 'collision' | 'gnss' | 'anchor' | 'notice';

/**
 * Which category each alarm key belongs to. Grouped by what the skipper actually
 * needs to hear: everything that means "the water/ground ahead is a problem" is
 * danger; the AIS CPA is its own collision sound; all the GNSS-integrity ones
 * share the caution chirp because they all mean the same thing (don't trust the
 * fix); the anchor nags on its own; and the routine nudges share the gentle blip.
 */
// Partial, like the map it replaces: AlarmKey technically includes the 'ais'
// container field (a quirk of keyof AlarmsLive) which is never a real alarm, and
// anything genuinely unmapped falls through to 'notice' below.
const CATEGORY_BY_KEY: Partial<Record<AlarmKey, AlarmSoundCategory>> = {
  // Standing into danger: shallow water, grounding ahead, a do-not-cross contour,
  // a crossed clearing bearing, a charted hazard close by.
  depth: 'danger',
  grounding: 'danger',
  clearingDepth: 'danger',
  clearing: 'danger',
  proximity: 'danger',
  // Closing traffic.
  aisCpa: 'collision',
  // The fix can't be trusted (all three mean the same thing to the ear).
  gnssLost: 'gnss',
  gnssJump: 'gnss',
  gnssAccuracy: 'gnss',
  // The anchor is dragging.
  anchor: 'anchor',
  // Low-priority nudges.
  tideHeight: 'notice',
  waypointArrival: 'notice',
  offTrack: 'notice',
  aisProximity: 'notice',
  lowBattery: 'notice',
};

/**
 * The category an alarm key belongs to. Falls back to 'notice' (the gentlest) for
 * any key not named above, so a newly-added alarm gets a quiet, safe sound by
 * default rather than accidentally shouting.
 */
export function alarmCategoryFor(key: AlarmKey): AlarmSoundCategory {
  return CATEGORY_BY_KEY[key] ?? 'notice';
}

/**
 * Default loudness per category (0..1). The dangerous, act-now categories run at
 * full volume; a GNSS caution is softer because it's a heads-up, not a grounding;
 * and the routine notices are quieter still so they never read as an emergency.
 */
export const CATEGORY_VOLUME: Record<AlarmSoundCategory, number> = {
  danger: 1,
  collision: 1,
  anchor: 1,
  gnss: 0.7,
  notice: 0.55,
};

/**
 * The tone id a category plays. It's the tone with the same id as the category
 * (see alarmTones.ts), so the mapping stays one-to-one and there's nothing to keep
 * in sync. Split out as a function so a future re-skin can point a category at a
 * different tone without changing the catalogue.
 */
export function categoryToneId(category: AlarmSoundCategory): ToneId {
  return category;
}

/** One alarm's default sound: the tone id and volume its category calls for. */
export interface CategorySound {
  tone: ToneId;
  volume: number;
}

/** The default tone + volume for an alarm key, via its category. */
export function defaultSoundForKey(key: AlarmKey): CategorySound {
  const category = alarmCategoryFor(key);
  return { tone: categoryToneId(category), volume: CATEGORY_VOLUME[category] };
}
